// One Socket.IO connection per tab. Server events refresh the cached queries
// and drive the live encounter prompt.

import { useEffect, useSyncExternalStore } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { QueryClient } from '@tanstack/react-query';
import { outfitAnimUrls, outfitAnimated, parseOutfitKey, type ClientToServerEvents, type EncounterPromptDTO, type MeDTO, type ServerToClientEvents } from '@lastday/shared';
import { qk } from '@/api/hooks';
import { crewChatInView } from '@/lib/crewChat';
import { toast } from '@/components/Toast';
import { t } from '@/i18n';
import { sfx } from '@/lib/sound/sound';

let socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;

// ---- Encounter prompt store --------------------------------------------------------
let prompt: EncounterPromptDTO | null = null;
const listeners = new Set<() => void>();
export const encounterStore = {
  get: () => prompt,
  set(p: EncounterPromptDTO | null) {
    prompt = p;
    listeners.forEach((l) => l());
  },
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};
export const useEncounterPrompt = () => useSyncExternalStore(encounterStore.subscribe, encounterStore.get, encounterStore.get);

// 23. 10. 2026 (owner, pilot report): a first-session run that came back (its report opens by itself, `ReportAutoOpen`).
let backRun: string | null = null;
const backListeners = new Set<() => void>();
export const returnedStore = {
  get: () => backRun,
  set(id: string | null) {
    backRun = id;
    backListeners.forEach((l) => l());
  },
  subscribe(l: () => void) {
    backListeners.add(l);
    return () => backListeners.delete(l);
  },
};
export const useFirstRunBack = () => useSyncExternalStore(returnedStore.subscribe, returnedStore.get, returnedStore.get);

// ---- Online counter ---------------------------------------------------------------
// "online": real players with a connection; "out there": runs and recent tracks (krug 3, T1).
let online = 0;
let outThere = 0;
const onlineListeners = new Set<() => void>();
const subscribeOnline = (l: () => void) => {
  onlineListeners.add(l);
  return () => onlineListeners.delete(l);
};
export const useOnlineCount = () => useSyncExternalStore(subscribeOnline, () => online);
export const useOutThereCount = () => useSyncExternalStore(subscribeOnline, () => outThere);

export function connectSocket(qc: QueryClient) {
  if (socket) {
    socket.disconnect();
  }
  const s: Socket<ServerToClientEvents, ClientToServerEvents> = io({ path: '/socket.io', withCredentials: true, transports: ['websocket', 'polling'] });
  socket = s;
  const inv = (...keys: readonly (readonly unknown[])[]) => keys.forEach((k) => qc.invalidateQueries({ queryKey: k }));

  s.on('survivor:update', () => inv(qk.me, qk.survivor, qk.skills));
  s.on('expedition:update', (p) => inv(qk.me, qk.expedition(p.id)));
  s.on('expedition:returned', (p) => {
    inv(qk.me, qk.survivor, qk.camp, qk.expedition(p.id), qk.expeditions, qk.inventory);
    if (p.first) returnedStore.set(p.id);
  });
  s.on('encounter:prompt', (p) => {
    encounterStore.set(p);
    inv(qk.me);
  });
  s.on('encounter:resolved', (p) => {
    const cur = encounterStore.get();
    if (cur && cur.id === p.id) encounterStore.set({ ...cur, resolved: true });
    inv(qk.me, qk.expedition(p.expeditionId));
  });
  s.on('feed:new', () => inv(['feed'], qk.camp));
  s.on('horde:update', () => inv(qk.horde, qk.world));
  s.on('camp:update', () => inv(qk.camp, qk.workshop, qk.me, ['contracts'], qk.trader));
  s.on('contracts:update', () => inv(['contracts']));
  s.on('season:update', () => inv(qk.season));
  s.on('event:prompt', (p) => {
    qc.setQueryData(['event', p.expeditionId], { prompt: p });
    inv(qk.me);
  });
  s.on('event:resolved', (p) => inv(qk.me, ['event', p.expeditionId], qk.expedition(p.expeditionId)));
  // 11. 10. 2026: a sale, a purchase or an expiry on the Market comes as an inventory update too.
  s.on('inventory:update', () => inv(qk.inventory, qk.workshop, ['collection'], qk.trader, ['market']));
  s.on('clan:update', () => {
    inv(qk.clanMine);
    inv(qk.crewBoards);
  });
  s.on('clan:chat', (p) => {
    inv(qk.clanMine);
    // 23. 10. 2026 (owner): a crewmate's message: /me has the unread count (the Crew entry's dot), unless the chat is open
    // and in view (it marks the message read there). Not for the player's own messages or the camp radio's lines.
    if (!p.system && p.userId !== qc.getQueryData<MeDTO>(qk.me)?.user?.id && !crewChatInView()) inv(qk.me);
  });
  // 23. 10. 2026: the chat was read on another tab or device: the dot goes here too.
  s.on('clan:read', () => inv(qk.me));
  s.on('notification', (n) => {
    // Krug 5 (K8): only kinds in the list refresh it and the red dot; some are a toast only.
    if (n.listed !== false) inv(qk.notifications, qk.me);
    if (n.kind === 'season') inv(qk.season);
    // Krug 5 (K16.2): a system opened: /me has the new list (and its New marks).
    if (n.kind === 'unlock') inv(qk.me);
    // Package 4, part L: a finished craft or upgrade has its sound.
    if (n.kind === 'craft') sfx('craft_done');
    if (n.toast === false) return;
    if (n.kind === 'outfit' || n.kind === 'outfit_legendary') {
      // A new outfit (docs/OUTFITI_garderoba.md, 4.5): "Try it on"; Legendary shows its first frame.
      inv(qk.survivor, qk.outfits, ['collection']);
      const o = parseOutfitKey(new URLSearchParams(n.link?.split('?')[1] ?? '').get('outfit'));
      const body = qc.getQueryData<MeDTO>(qk.me)?.survivor?.bodyType;
      toast.outfit(n.title, n.link, n.body, o?.rarity === 'legendary' && body && outfitAnimated(body, o.set) ? outfitAnimUrls(body, o.set).poster : undefined);
    } else toast.note(n.title, n.body, n.link, toastAction(n.kind, n.link));
  });
  s.on('online', (p) => {
    online = p.online;
    outThere = p.outThere ?? 0;
    onlineListeners.forEach((l) => l());
  });
  s.on('connect', () => s.emit('presence:ping'));
  return s;
}

/** The button on a toast that is not in the list (K8): where the thing is waiting. */
function toastAction(kind: string, link: string | null): string | undefined {
  if (kind === 'craft') return t(link?.startsWith('/inventory/') ? 'settings.notifications.action.item' : 'settings.notifications.action.workshop');
  if (kind === 'building') return t('settings.notifications.action.building');
  if (kind === 'skill_point') return t('settings.notifications.action.skills');
  if (kind === 'scout' || kind === 'ghost_held') return t('settings.notifications.action.camp');
  // Package 4, part E (6): a defeat of your tracks or a theft: the button is the revenge.
  if ((kind === 'ghost' || kind === 'gear_taken') && link?.startsWith('/revenge/')) return t('settings.notifications.action.revenge');
  // Krug 5 (K15.2): a season goal done: the toast opens the season path to claim it.
  if (kind === 'season' && link === '/season') return t('season.action');
  if (kind === 'unlock') return t('common.unlockAction');
  return undefined;
}

/** Connects once the player is signed in, reconnects when the account changes. */
export function useRealtime(qc: QueryClient, userId: string | null | undefined) {
  useEffect(() => {
    if (!userId) return;
    const s = connectSocket(qc);
    const t = setInterval(() => s.emit('presence:ping'), 60_000);
    return () => {
      clearInterval(t);
      s.disconnect();
      if (socket === s) socket = null;
    };
  }, [qc, userId]);
}
